import React, { useState, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { GlobalStyle } from "./components/globalStyle";
import { Tween } from "react-gsap";
import ReactFullpage from "@fullpage/react-fullpage";
import {
  SideNav,
  ImageTween,
  Image360,
  Steps,
  Video,
  Header
} from "./components";
import { Container } from "./components/layoutComponents";
import { Items } from "./items";

const useDebounce = (value, delay) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  return debouncedValue;
};

const TouchHandler = ({ fullpageApi }) => {
  const [scroll, setScroll] = useState("false");
  const debouncedSearchTerm = useDebounce(scroll, 1000);

  useEffect(() => {
    if (debouncedSearchTerm === "up") {
      fullpageApi.moveSectionUp();
    } else if (debouncedSearchTerm === "down") {
      fullpageApi.moveSectionDown();
    }
  }, [debouncedSearchTerm]);
  let start = null;

  window.addEventListener("touchstart", e => {
    start = e.changedTouches[0];
  });

  window.addEventListener("touchend", e => {
    let end = e.changedTouches[0];
    if (end && start && end.screenY - start.screenY > 50) {
      setScroll("up");
    } else if (end && start && end.screenY - start.screenY < -50) {
      setScroll("down");
    }
    setTimeout(() => {
      setScroll("false");
    }, 1500);
  });
  return null;
};

function App() {
  const [activeNav, setActiveNav] = useState("start");
  const [active, setActive] = useState(null);
  const [hover, setHover] = useState(null);
  const [api, setApi] = useState(null);

  const getFrames = (key, options) => {
    let frames = undefined;
    if (active) {
      const activeIndex = Items[key].findIndex(a => a.key === active.key);
      if (active && Items[key][activeIndex]) {
        frames = active.frames;
      }
      if (
        active &&
        Items[key][activeIndex] &&
        options &&
        options.startFrame &&
        active.startFrame
      ) {
        return active.startFrame;
      }
      if (options && options.next) {
        const found = Items[key][activeIndex + 1];
        return found ? found.frames : undefined;
      }
      if (options && options.prev) {
        const found = Items[key][activeIndex - 1];
        return found ? found.frames : undefined;
      }
    }
    return frames;
  };

  const setActiveNavItem = hash => {
    const els = document.querySelectorAll(".navItem.active");
    for (var i = 0; i < els.length; i++) {
      els[i].classList.remove("active");
    }
    const v = document.getElementById(`nav-${hash}`);
    if (v) {
      v.className += " active";
    }
  };

  return (
    <ThemeProvider
      theme={{
        colors: {
          primary: "#E0003C",
          link: "#1b3067",
          text: "#47443f",
          lightText: "#8a888",
          highlight: "#1562ad"
        },
        break: 1024
      }}
    >
      <GlobalStyle />
      <div className="gd-bem">
        <Header />
        <div className="fixedWrap">
          <div style={{ width: "100%", position: "relative" }}>
            <TouchHandler fullpageApi={api} />

            <Container>
              <aside className={activeNav}>
                <h1 className="canHide hideMobile">QIAcuity</h1>
                <p className="canHide" style={{ marginTop: 10 }}>
                  Nanoplate-based digital PCR system
                </p>
                {activeNav === "start" || activeNav === "contact" ? (
                  <Tween
                    from={{ opacity: 0 }}
                    to={{ opacity: 1 }}
                    className="hideMobile"
                    fullpageApi={api}
                  >
                    <h2 style={{ marginTop: 30 }}>The magic is inside</h2>
                  </Tween>
                ) : null}
                <SideNav
                  items={Items[activeNav]}
                  active={active ? active.key : undefined}
                  activeNav={activeNav}
                  fullpageApi={api}
                  hover={hover}
                  setHover={setHover}
                />
                {activeNav === "contact" ? (
                  <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
                    <div className="contactBox">
                      <div className="row" style={{ marginTop: "2rem" }}>
                        <div className="col">
                          <h2>Would you like to receive more information?</h2>
                          <div className="row">
                            <a
                              href="https://www.qiagen.com/applications/digital-pcr/qiacuity-demo-or-quote-request?intcmp=CM_PCR_dPCR_0322_RD_CW"
                              className="qi-button"
                              target="_blank"
                            >
                              Request Demo
                            </a>
                            <a
                              href="https://www.qiagen.com/products/instruments-and-automation/pcr-instruments/qiacuity-digital-pcr-system"
                              className="qi-button qi-button--inverted"
                              target="_blank"
                              style={{ marginLeft: 10 }}
                            >
                              Visit Webpage
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tween>
                ) : null}
                {activeNav === "start" || activeNav === "contact" ? (
                  <p className="small" style={{ marginTop: 35 }}>
                    This virtual demo is for representation purpose only, so
                    slight variation in actual color vs. rendered image is
                    possible.
                  </p>
                ) : null}
              </aside>
              <div
                className={`sceneContent start ${
                  activeNav === "start" ? "hidable" : " hidable hide"
                }`}
              >
                {activeNav === "start" ? (
                  <Image360
                    path="QIAcuity_One_360_2/Qia_one_360_000"
                    frames={240}
                    active={activeNav === "start"}
                  />
                ) : null}
              </div>
              <div
                className={`sceneContent ${
                  activeNav === "components" ? "hidable" : " hidable hide"
                }`}
              >
                {activeNav === "components" ? (
                  <ImageTween
                    type="components"
                    path="Components/Components_0000"
                    active={activeNav === "components"}
                    startFrame={
                      activeNav === "components"
                        ? getFrames("components", { startFrame: true })
                        : undefined
                    }
                    frames={
                      activeNav === "components"
                        ? getFrames("components")
                        : undefined
                    }
                    nextFrames={
                      activeNav === "components"
                        ? getFrames("components", { next: true })
                        : undefined
                    }
                    prevFrames={
                      activeNav === "components"
                        ? getFrames("components", { prev: true })
                        : undefined
                    }
                    totalFrames={950}
                    tips={active ? active.tips : undefined}
                    texts={active ? active.texts : undefined}
                    classNames={active ? active.classNames : undefined}
                    setHover={setHover}
                    hover={hover}
                    fullpageApi={api}
                  />
                ) : null}
              </div>
              <div
                className={`sceneContent ${
                  activeNav === "features" ? "hidable" : " hidable hide"
                }`}
              >
                {activeNav === "features" ? (
                  <ImageTween
                    type="features"
                    active={activeNav === "features"}
                    frames={
                      activeNav === "features"
                        ? getFrames("features")
                        : undefined
                    }
                    startFrame={
                      activeNav === "features"
                        ? getFrames("features", { startFrame: true })
                        : undefined
                    }
                    nextFrames={
                      activeNav === "features"
                        ? getFrames("features", { next: true })
                        : undefined
                    }
                    prevFrames={
                      activeNav === "features"
                        ? getFrames("features", { prev: true })
                        : undefined
                    }
                    path="Features_Benefits/Features_Benefits_000"
                    totalFrames={397}
                    toReplace={3}
                    tips={active ? active.tips : undefined}
                    texts={active ? active.texts : undefined}
                    classNames={active ? active.classNames : undefined}
                  />
                ) : null}
              </div>

              <div
                className={`sceneContent ${
                  active && active.key && active.key.indexOf("video") > -1
                    ? "workflow"
                    : ""
                } ${activeNav === "workflow" ? "hidable" : " hidable hide"}`}
              >
                <Steps
                  active={active ? active.key : undefined}
                  fullpageApi={api}
                />
              </div>

              <div
                className={`sceneContent contact ${
                  activeNav === "contact" ? "hidable" : " hidable hide"
                }`}
              >
                <img
                  src={require(`./images/contact_3d.jpg`)}
                  alt="QIAcuity"
                  width="100%"
                  height="auto"
                />
              </div>
            </Container>
            {activeNav === "workflow" &&
            active &&
            active.key === "preparation_video" ? (
              <Video
                active={active && active.key === "preparation_video"}
                source={require(`./videos/workflow_1.mp4`)}
                fullpageApi={api}
              />
            ) : null}
            {activeNav === "workflow" &&
            active &&
            active.key === "partitioning_video" ? (
              <Video
                active={active && active.key === "partitioning_video"}
                source={require(`./videos/workflow_2.mp4`)}
                fullpageApi={api}
              />
            ) : null}
            {activeNav === "workflow" &&
            active &&
            active.key === "analysis_video" ? (
              <Video
                active={active && active.key === "analysis_video"}
                source={require(`./videos/workflow_3.mp4`)}
                fullpageApi={api}
              />
            ) : null}
          </div>
        </div>
        <ReactFullpage
          onLeave={(origin, destination, direction) => {
            const { index, anchor, item } = destination;

            const splittted = anchor.split("-");
            const nav = splittted[0];
            setActiveNav(nav);
            setActiveNavItem(nav);
            if (splittted[1]) {
              const activeItem = Items[nav].find(i => i.key === splittted[1]);
              setActive(activeItem);
            }
          }}
          autoScrolling
          responsiveSlides
          licenseKey="4F586536-17CF4DDA-A766844B-CE2236FC"
          scrollingSpeed={2000} /* Options here */
          dragAndMove
          render={({ state, fullpageApi }) => {
            if (!api) {
              setApi(fullpageApi);
            }
            return (
              <ReactFullpage.Wrapper>
                <div className="section" data-anchor="start" />

                <div className="section" data-anchor="components-start" />
                <div className="section" data-anchor="components-nanoplate" />
                <div className="section" data-anchor="components-touchscreen" />
                <div className="section" data-anchor="components-puncher" />
                <div className="section" data-anchor="components-barcode" />
                <div className="section" data-anchor="components-optical" />
                <div className="section" data-anchor="components-tray" />
                <div
                  className="section"
                  data-anchor="components-thermocycler"
                />
                <div className="section" data-anchor="components-software" />

                <div className="section" data-anchor="features-accuracy" />
                <div className="section" data-anchor="features-accuracyBack" />
                <div className="section" data-anchor="features-scalability" />
                <div className="section" data-anchor="features-throughput" />
                <div className="section" data-anchor="features-multiplexing" />
                <div className="section" data-anchor="features-remote" />

                <div className="section" data-anchor="workflow-preparation" />
                <div
                  className="section"
                  data-anchor="workflow-preparation_video"
                />
                <div className="section" data-anchor="workflow-partitioning" />
                <div
                  className="section"
                  data-anchor="workflow-partitioning_video"
                />
                <div className="section" data-anchor="workflow-analysis" />
                <div
                  className="section"
                  data-anchor="workflow-analysis_video"
                />

                <div className="section" data-anchor="contact" />
              </ReactFullpage.Wrapper>
            );
          }}
        />
        <div
          className={`scroll-downs  ${
            activeNav !== "contact" ? "hidable" : " hidable hide"
          }`}
        >
          <div className="mousey">
            <span>Scroll to navigate</span>
            <span className="arrow-down" />
            <img
              src={require(`./images/downwards-pointer.svg`).default}
              alt="QIAcuity"
              className="arrow-down"
            />
            {/* <div className="scroller"></div> */}
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
